import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export const GROUP_COLOR_FALLBACK = "bg-slate-400";

export const safeJsonParse = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

export const isDemoUser = (user) => Number(user?.role_id) === 4;

export const formatNumber = (value) =>
  new Intl.NumberFormat("fr-FR").format(Number(value || 0));

export const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const formatDateForInput = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const getAdherentName = (adherent) => {
  const prenom = adherent?.prenom || "";
  const nom = adherent?.nom || "";

  return `${prenom} ${nom}`.trim() || "Adhérent";
};

export const getInitials = (adherent) => {
  const prenom = adherent?.prenom?.trim()?.[0] || "";
  const nom = adherent?.nom?.trim()?.[0] || "";

  return `${prenom}${nom}`.toUpperCase() || "AD";
};

export const buildGroupColorMap = (groups = []) => {
  return groups.reduce((acc, group) => {
    if (!group?.nom) return acc;

    acc[group.nom] = group.color_groupe || GROUP_COLOR_FALLBACK;

    return acc;
  }, {});
};

export const getGroupColorClass = (groupColorMap, groupName) => {
  const colorClass = groupColorMap?.[groupName];

  if (!colorClass || colorClass === "bg-white") {
    return "bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700";
  }

  if (colorClass === "bg-black") {
    return "bg-slate-950 text-white dark:bg-white dark:text-slate-950";
  }

  return `${colorClass} text-white`;
};

export const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const filterAndSortAdherents = (adherents = [], filters = {}) => {
  const query = normalizeText(filters.query);
  const selectedGroup = filters.group;

  let result = [...adherents];

  if (selectedGroup) {
    result = result.filter(
      (adherent) => adherent.adherent_groupe === selectedGroup
    );
  }

  if (query) {
    result = result.filter((adherent) => {
      const haystack = normalizeText(
        [
          adherent.nom,
          adherent.prenom,
          adherent.email,
          adherent.tel,
          adherent.adherent_groupe,
          adherent.licence_number,
          adherent.commune,
        ]
          .filter(Boolean)
          .join(" ")
      );

      return haystack.includes(query);
    });
  }

  result.sort((a, b) => {
    const nameA = normalizeText(`${a.nom || ""} ${a.prenom || ""}`);
    const nameB = normalizeText(`${b.nom || ""} ${b.prenom || ""}`);

    if (filters.sortOrder === "name_desc") {
      return nameB.localeCompare(nameA);
    }

    if (filters.sortOrder === "created_desc") {
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    }

    if (filters.sortOrder === "created_asc") {
      return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
    }

    return nameA.localeCompare(nameB);
  });

  return result;
};

export const buildInscriptionsStats = ({
  adherents = [],
  filteredAdherents = [],
  groups = [],
}) => {
  const withEmail = adherents.filter((adherent) => adherent.email).length;
  const withLicence = adherents.filter(
    (adherent) => adherent.licence_number
  ).length;
  const photoAllowed = adherents.filter(
    (adherent) => Boolean(adherent.autorisation_photo)
  ).length;

  return {
    total: adherents.length,
    filtered: filteredAdherents.length,
    groups: groups.length,
    withEmail,
    withLicence,
    photoAllowed,
  };
};

export const normalizePhone = (value) => String(value || "").trim();

export const normalizeWeight = (value) => {
  if (value === null || value === undefined || value === "") return "";

  return String(value).replace(",", ".").trim();
};

export const normalizeAdherentPayload = (formData) => ({
  ...formData,
  nom: String(formData.nom || "").trim().toUpperCase(),
  prenom: String(formData.prenom || "").trim(),
  email: String(formData.email || "").trim(),
  email_secondaire: String(formData.email_secondaire || "").trim(),
  tel: normalizePhone(formData.tel),
  tel_secondaire: normalizePhone(formData.tel_secondaire),
  adresse: String(formData.adresse || "").trim(),
  cp: String(formData.cp || "").trim(),
  commune: String(formData.commune || "").trim(),
  poids: normalizeWeight(formData.poids),
  licence_number: String(formData.licence_number || "").trim().toUpperCase(),
  notes_adherent: String(formData.notes_adherent || "").trim(),
  autorisation_photo: Boolean(formData.autorisation_photo),
});

export const validateAdherentForm = (formData) => {
  const requiredFields = [
    ["adherent_groupe", "Le groupe est obligatoire."],
    ["nom", "Le nom est obligatoire."],
    ["prenom", "Le prénom est obligatoire."],
    ["email", "L'email est obligatoire."],
    ["tel", "Le téléphone est obligatoire."],
    ["adresse", "L'adresse est obligatoire."],
    ["cp", "Le code postal est obligatoire."],
    ["commune", "La commune est obligatoire."],
    ["genre", "Le genre est obligatoire."],
    ["date_naissance", "La date de naissance est obligatoire."],
  ];

  for (const [field, message] of requiredFields) {
    if (!String(formData[field] || "").trim()) {
      return message;
    }
  }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailPattern.test(formData.email)) {
    return "Veuillez saisir un email valide.";
  }

  if (
    formData.email_secondaire &&
    !emailPattern.test(formData.email_secondaire)
  ) {
    return "Veuillez saisir un email secondaire valide.";
  }

  const birthDate = new Date(formData.date_naissance);
  const today = new Date();
  const minDate = new Date(1900, 0, 1);

  if (
    Number.isNaN(birthDate.getTime()) ||
    birthDate >= today ||
    birthDate < minDate
  ) {
    return "Veuillez saisir une date de naissance valide.";
  }

  const cpPattern = /^[0-9]{5}$/;

  if (!cpPattern.test(String(formData.cp || ""))) {
    return "Veuillez saisir un code postal valide à 5 chiffres.";
  }

  const phonePattern = /^(?:(?:\+33|0)[1-9])(?:[\s.-]?\d{2}){4}$/;

  if (!phonePattern.test(String(formData.tel || "").trim())) {
    return "Veuillez saisir un téléphone valide.";
  }

  if (
    formData.tel_secondaire &&
    !phonePattern.test(String(formData.tel_secondaire || "").trim())
  ) {
    return "Veuillez saisir un téléphone secondaire valide.";
  }

  return null;
};

export const getAdherentExportRows = (adherents = []) =>
  adherents.map((adherent) => ({
    Nom: adherent.nom || "",
    Prénom: adherent.prenom || "",
    Groupe: adherent.adherent_groupe || "",
    Email: adherent.email || "",
    "Email secondaire": adherent.email_secondaire || "",
    Téléphone: adherent.tel || "",
    "Téléphone secondaire": adherent.tel_secondaire || "",
    Adresse: adherent.adresse || "",
    CP: adherent.cp || "",
    Commune: adherent.commune || "",
    Genre: adherent.genre || "",
    "Date de naissance": formatDate(adherent.date_naissance),
    Poids: adherent.poids || "",
    Licence: adherent.licence_number || "",
    "Autorisation photo": adherent.autorisation_photo ? "Oui" : "Non",
    Notes: adherent.notes_adherent || "",
  }));

export const exportAdherentsToExcel = (adherents = []) => {
  const worksheet = XLSX.utils.json_to_sheet(getAdherentExportRows(adherents));
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, "Adherents");

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  const data = new Blob([excelBuffer], {
    type: "application/octet-stream",
  });

  saveAs(data, "adherents_inscrits.xlsx");
};

export const exportAdherentsToPDF = (adherents = [], group = "") => {
  const doc = new jsPDF();

  const title = group
    ? `Liste des adhérents - ${group}`
    : "Liste des adhérents inscrits";

  doc.setFontSize(14);
  doc.text(title, 14, 15);

  doc.setFontSize(9);
  doc.text(`Généré le ${new Date().toLocaleDateString("fr-FR")}`, 14, 22);

  doc.autoTable({
    startY: 28,
    head: [["Nom", "Prénom", "Groupe", "Email", "Téléphone"]],
    body: adherents.map((adherent) => [
      adherent.nom || "",
      adherent.prenom || "",
      adherent.adherent_groupe || "",
      adherent.email || "",
      adherent.tel || "",
    ]),
    styles: {
      fontSize: 8,
      cellPadding: 2,
    },
    headStyles: {
      fillColor: [15, 23, 42],
    },
  });

  doc.save("adherents_inscrits.pdf");
};